// ─────────────────────────────────────────────────────────────────────────────
// TaskDetailDrawer, full interactive task drawer, ported 1:1 from the LEGACY
// window.acTasks drawer (site/js/tasks-drawer.js) into React.
//
// Feature parity with legacy: editable title + notes, the STATUS pill picker
// (Open / In progress / Needs review / Done) that persists, editable assignees
// (chip row + contact picker) + the assigner (initiator) chip, SUBTASKS with
// checkboxes + parent auto-complete, the APPROVAL section (needs_review → Approve
// / Request revisions, gated to the initiator only, Bible Ch.07), the LINKS
// list, the ACTIVITY log (created / status / comment events, relative time), the
// COMMENTS list + composer (optimistic), the visible-to pills (initiator-only),
// the completion banner + "save permanently", and initiator-gated delete.
//
// SCOPE (Bible 06.4): all reads/writes flow through lib/tasks with the band id
// passed by the caller (roster surface → the selected artist's X-Band-Id) or the
// ambient band (artist's own surface / release page). Optimistic writes confirm
// on a real 2xx and roll back on failure (no fabricated success).
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import {
  fetchDrawerTask,
  saveDrawerTask,
  deleteDrawerTask,
  STANDALONE_STATUS_WHITELIST,
} from '../../lib/tasks';
import type {
  DrawerTask,
  TaskPerson,
  TaskSubtask,
  TaskComment,
  TaskLink,
  TaskActivity,
  TaskVisibility,
} from '../../lib/tasks';
import { ImgFill } from './ImgFill';
import './TaskDetailDrawer.css';

// ── Status option sets (legacy STATUS_OPTIONS / STATUS_OPTIONS_STANDALONE) ────
interface StatusOpt { key: string; label: string; dot: string }
const STATUS_RELEASE: readonly StatusOpt[] = [
  { key: 'not_started', label: 'Not started', dot: '#9ca3af' },
  { key: 'in_progress', label: 'In progress', dot: '#3b82f6' },
  { key: 'needs_review', label: 'Needs review', dot: '#f59e0b' },
  { key: 'done', label: 'Done', dot: '#16a34a' },
];
const STATUS_STANDALONE: readonly StatusOpt[] = [
  { key: 'open', label: 'Open', dot: '#9ca3af' },
  { key: 'in_progress', label: 'In progress', dot: '#3b82f6' },
  { key: 'needs_review', label: 'Needs review', dot: '#f59e0b' },
  { key: 'done', label: 'Done', dot: '#16a34a' },
];
const ALL_STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started', open: 'Open', in_progress: 'In progress',
  needs_review: 'Needs review', blocked: 'Blocked', done: 'Done',
  approved: 'Approved', revisions_requested: 'Revisions requested',
};

// ── Small formatting helpers (mirror legacy fmt*). ────────────────────────────
function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function fmtRelTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const dys = Math.floor(h / 24);
  if (dys < 7) return dys + 'd ago';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function hostnameOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}
function personName(p: TaskPerson | null | undefined): string {
  if (!p) return '';
  const first = p.firstName || p.name || '';
  return first + (p.lastName ? ' ' + p.lastName : '');
}
function personInitials(p: TaskPerson | null | undefined): string {
  if (!p) return '?';
  if (p.initials) return p.initials.toUpperCase().slice(0, 2);
  const first = p.firstName || p.name || p.email || '?';
  const two = (first.charAt(0) || '') + (p.lastName ? p.lastName.charAt(0) : '');
  return (two || first.charAt(0)).toUpperCase().slice(0, 2) || '?';
}
function avatarOf(p: TaskPerson | null | undefined): string {
  return (p && (p.avatarUrl || p.avatar_url || p.photo_url)) || '';
}
function newId(prefix: string): string {
  return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ── Current user identity (for initiator-only gates + activity actor). ─────────
export interface DrawerUser {
  readonly id?: string | number;
  readonly name?: string;
  readonly avatarUrl?: string;
  readonly email?: string;
}

export interface TaskDetailDrawerProps {
  /** Composite/standalone task id (st_… or releaseId:idx). */
  readonly taskId: string;
  /** Roster surface: the selected artist's band id (→ X-Band-Id). Omit on the
   *  artist's own surface / release page (ambient band). */
  readonly artistId?: string;
  /** Signed-in user, drives approval/delete gates + activity actor stamping. */
  readonly currentUser?: DrawerUser | null;
  readonly onClose: () => void;
  /** Fired after any confirmed save/delete so the opener can refetch its rows. */
  readonly onChanged?: () => void;
}

// ── Person chip ────────────────────────────────────────────────────────────────
function PersonChip({ person, onClick, empty }: {
  readonly person?: TaskPerson | null;
  readonly onClick?: () => void;
  readonly empty?: string;
}): JSX.Element {
  if (empty) {
    return (
      <button type="button" className="acTd__assignee-chip acTd__assignee-chip--empty" onClick={onClick}>
        {empty}
      </button>
    );
  }
  const name = personName(person) || person?.email || '?';
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={'acTd__assignee-chip' + (onClick ? '' : ' acTd__assignee-chip--static')}
      onClick={onClick}
    >
      <span className="acTd__avatar">
        {personInitials(person)}
        <ImgFill src={avatarOf(person)} alt="" />
      </span>
      <span>{name}</span>
    </Tag>
  );
}

// ── Assignee/contact picker dropdown ──────────────────────────────────────────
function ContactPicker({ contacts, exclude, allowClear, onPick, onClose }: {
  readonly contacts: readonly TaskPerson[];
  readonly exclude?: ReadonlySet<string>;
  readonly allowClear?: boolean;
  readonly onPick: (c: TaskPerson | null) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [onClose]);
  const list = contacts.filter((c) => {
    if (exclude && c.id && exclude.has(String(c.id))) return false;
    if (!q) return true;
    return personName(c).toLowerCase().includes(q.toLowerCase());
  });
  return (
    <div className="acTd__picker" ref={ref} style={{ top: '100%', left: 0, marginTop: 6 }}>
      <input
        className="acTd__picker-search"
        placeholder="Search…"
        value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
      />
      {allowClear && (
        <div className="acTd__picker-item acTd__picker-item--clear" onMouseDown={() => onPick(null)}>
         , Unassigned
        </div>
      )}
      {list.map((c) => (
        <div className="acTd__picker-item" key={String(c.id)} onMouseDown={() => onPick(c)}>
          <span className="acTd__avatar">
            {personInitials(c)}
            <ImgFill src={avatarOf(c)} alt="" />
          </span>
          <span>{personName(c)}</span>
          {c.role && <span className="acTd__picker-meta">{c.role.replace(/_/g, ' ')}</span>}
        </div>
      ))}
    </div>
  );
}

// ── The drawer ─────────────────────────────────────────────────────────────────
export function TaskDetailDrawer({
  taskId,
  artistId,
  currentUser,
  onClose,
  onChanged,
}: TaskDetailDrawerProps): JSX.Element {
  const [task, setTask] = useState<DrawerTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assigneePicker, setAssigneePicker] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [subtaskText, setSubtaskText] = useState('');
  const [commentText, setCommentText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const notesTimer = useRef<number | undefined>(undefined);

  const meId = currentUser?.id != null ? String(currentUser.id) : '';
  const meName = currentUser?.name || currentUser?.email || 'You';

  // ── Load full record on open. ──
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    void fetchDrawerTask(taskId, artistId).then((t) => {
      if (!alive) return;
      setLoading(false);
      if (t) setTask(t); else setNotFound(true);
    });
    return () => { alive = false; };
  }, [taskId, artistId]);

  // ── Esc closes. ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Persist a next-state task with optimistic UI + rollback on failure. ──
  const persist = useCallback(async (next: DrawerTask, prev: DrawerTask): Promise<void> => {
    setTask(next);
    setSaving(true);
    const ok = await saveDrawerTask(next);
    setSaving(false);
    if (!ok) { setTask(prev); return; }
    onChanged?.();
  }, [onChanged]);

  const statusOpts = task?.standalone ? STATUS_STANDALONE : STATUS_RELEASE;

  // Append an activity entry (mirrors legacy _log).
  const logActivity = useCallback((action: string, from: unknown, to: unknown): TaskActivity => ({
    ts: new Date().toISOString(), actor_id: meId, actor_name: meName, action, from, to,
  }), [meId, meName]);

  // ── Field mutations ──
  function saveTitle(val: string): void {
    if (!task || val === task.title) return;
    const prev = task;
    persist({ ...task, title: val, activity: [...task.activity, logActivity('title', task.title, val)] }, prev);
  }
  function saveNotes(val: string): void {
    if (!task || val === task.notes) return;
    const prev = task;
    persist({ ...task, notes: val, activity: [...task.activity, logActivity('notes', task.notes.length, val.length)] }, prev);
  }
  function saveDue(val: string): void {
    if (!task) return;
    const next = val ? val.slice(0, 10) : '';
    if (next === task.due) return;
    const prev = task;
    persist({ ...task, due: next, activity: [...task.activity, logActivity('due', task.due, next)] }, prev);
  }
  function setStatus(v: string): void {
    if (!task || v === task.status) return;
    const prev = task;
    const now = new Date().toISOString();
    const terminal = v === 'done' || v === 'approved';
    persist({
      ...task,
      status: v,
      done: terminal,
      completed_at: terminal ? now : '',
      completed_by: terminal ? { id: meId, name: meName, avatar_url: currentUser?.avatarUrl || '' } : null,
      activity: [...task.activity, logActivity('status', task.status, v)],
    }, prev);
  }
  function toggleVisibility(key: keyof TaskVisibility): void {
    if (!task) return;
    const prev = task;
    const nextVis: TaskVisibility = { ...task.visibility, [key]: !task.visibility[key] };
    persist({ ...task, visibility: nextVis, activity: [...task.activity, logActivity('visibility', task.visibility, nextVis)] }, prev);
  }
  function archiveForever(): void {
    if (!task) return;
    const prev = task;
    persist({ ...task, archived_forever: true, activity: [...task.activity, logActivity('archived_forever', false, true)] }, prev);
  }

  // ── Subtasks (auto-complete parent when all done) ──
  function commitSubtasks(subs: TaskSubtask[]): void {
    if (!task) return;
    const prev = task;
    let status = task.status;
    let done = task.done;
    const activity = [...task.activity];
    if (subs.length) {
      const allDone = subs.every((s) => s.done);
      if (allDone && status !== 'done') { activity.push(logActivity('status', status, 'done')); status = 'done'; done = true; }
      else if (!allDone && status === 'done') { activity.push(logActivity('status', status, 'in_progress')); status = 'in_progress'; done = false; }
    }
    const terminal = status === 'done' || status === 'approved';
    persist({
      ...task, subtasks: subs, status, done,
      completed_at: terminal ? (task.completed_at || new Date().toISOString()) : '',
      completed_by: terminal ? (task.completed_by || { id: meId, name: meName, avatar_url: currentUser?.avatarUrl || '' }) : null,
      activity,
    }, prev);
  }
  function addSubtask(): void {
    const v = subtaskText.trim();
    if (!v || !task) return;
    setSubtaskText('');
    commitSubtasks([...task.subtasks, { id: newId('sub_'), title: v.slice(0, 300), done: false }]);
  }
  function toggleSubtask(id: string): void {
    if (!task) return;
    commitSubtasks(task.subtasks.map((s) => (s.id === id ? { ...s, done: !s.done } : s)));
  }
  function delSubtask(id: string): void {
    if (!task) return;
    commitSubtasks(task.subtasks.filter((s) => s.id !== id));
  }

  // ── Assignees (standalone = multi; release = single) ──
  // Multi-assignee for BOTH standalone AND release-checklist tasks, append the
  // picked contact (deduped) and keep `assignee` = the first for back-compat with
  // any single-assignee reader. (The release checklist item persists assignees[].)
  function pickAssignee(c: TaskPerson | null): void {
    setAssigneePicker(false);
    if (!task || !c) return;
    if (task.assignees.some((a) => String(a.contactId ?? a.id) === String(c.id))) return;
    const added: TaskPerson = { contactId: c.id, firstName: c.firstName, lastName: c.lastName, initials: personInitials(c), avatarUrl: avatarOf(c) };
    const nextAssignees = [...task.assignees, added];
    persist({ ...task, assignees: nextAssignees, assignee: nextAssignees[0], activity: [...task.activity, logActivity('assignee_add', null, personName(c))] }, task);
  }
  function removeAssignee(idx: number): void {
    if (!task) return;
    const prev = task;
    const removed = task.assignees[idx];
    const next = task.assignees.filter((_, i) => i !== idx);
    persist({ ...task, assignees: next, assignee: next[0] || null, activity: [...task.activity, logActivity('assignee_remove', personName(removed), null)] }, prev);
  }

  // ── Approval (initiator-only) ──
  function approve(): void {
    if (!task) return;
    const prev = task;
    const now = new Date().toISOString();
    const comment: TaskComment = { id: newId('c_'), ts: now, actor_id: meId, actor_name: meName, body: 'Approved by reviewer.' };
    persist({
      ...task, status: 'approved', done: true, completed_at: now,
      completed_by: { id: meId, name: meName, avatar_url: currentUser?.avatarUrl || '' },
      approval: { ...task.approval, status: 'approved', approver_id: meId, decided_at: now },
      comments: [...task.comments, comment],
      activity: [...task.activity, logActivity('status', task.status, 'approved')],
    }, prev);
  }
  function requestRevisions(): void {
    if (!task) return;
    const notes = window.prompt('What revisions are needed? This will be sent back to the assignees as a comment.');
    if (!notes || !notes.trim()) return;
    const prev = task;
    const now = new Date().toISOString();
    const comment: TaskComment = { id: newId('c_'), ts: now, actor_id: meId, actor_name: meName, body: 'Revisions requested: ' + notes.trim() };
    persist({
      ...task, status: 'revisions_requested',
      approval: { ...task.approval, status: 'revisions_requested', approver_id: meId, decided_at: now, revision_notes: notes.trim() },
      comments: [...task.comments, comment],
      activity: [...task.activity, logActivity('status', task.status, 'revisions_requested')],
    }, prev);
  }

  // ── Links ──
  function addLink(): void {
    if (!task) return;
    const url = linkUrl.trim();
    if (!url) return;
    const full = /^https?:\/\//i.test(url) ? url : 'https://' + url;
    const link: TaskLink = { id: newId('l_'), url: full, label: linkLabel.trim() || hostnameOf(full) || full, ts: new Date().toISOString() };
    setLinkUrl(''); setLinkLabel(''); setShowLinkForm(false);
    const prev = task;
    persist({ ...task, links: [...task.links, link], activity: [...task.activity, logActivity('link_add', null, link.label)] }, prev);
  }
  function removeLink(id: string): void {
    if (!task) return;
    const prev = task;
    const removed = task.links.find((l) => l.id === id);
    persist({ ...task, links: task.links.filter((l) => l.id !== id), activity: [...task.activity, logActivity('link_remove', removed?.label, null)] }, prev);
  }

  // ── Comments (optimistic → confirm/rollback) ──
  async function addComment(): Promise<void> {
    if (!task) return;
    const body = commentText.trim();
    if (!body) return;
    setCommentText('');
    const optimistic: TaskComment = { id: newId('tmp_'), ts: new Date().toISOString(), actor_id: meId, actor_name: meName, body, _pending: true };
    const baseline = task;
    const withPending = { ...task, comments: [...task.comments, optimistic] };
    setTask(withPending);
    const real: TaskComment = { id: newId('c_'), ts: optimistic.ts, actor_id: meId, actor_name: meName, body };
    const next: DrawerTask = { ...baseline, comments: [...baseline.comments, real] };
    setSaving(true);
    const ok = await saveDrawerTask(next);
    setSaving(false);
    if (ok) { setTask(next); onChanged?.(); }
    else {
      // Keep the optimistic comment visible, flagged failed (no fabricated success).
      setTask({ ...baseline, comments: [...baseline.comments, { ...optimistic, _pending: false, _failed: true, _error: 'Save failed', actor_name: meName + ' (not saved)' }] });
    }
  }
  function removeComment(id: string): void {
    if (!task) return;
    const prev = task;
    persist({ ...task, comments: task.comments.filter((c) => c.id !== id) }, prev);
  }

  // ── Delete ──
  async function doDelete(): Promise<void> {
    if (!task) return;
    setConfirmDelete(false);
    setSaving(true);
    const ok = await deleteDrawerTask(task);
    setSaving(false);
    if (ok) { onChanged?.(); onClose(); }
  }

  // ── Derived render state ──
  const resolvedStatus = task ? task.status : '';
  const pillKeys = new Set(statusOpts.map((o) => o.key));
  const showStatusBadge = task ? !pillKeys.has(resolvedStatus) : false;
  const badgeColor = resolvedStatus === 'approved' ? '#16a34a' : resolvedStatus === 'revisions_requested' ? '#f59e0b' : '#6b7280';

  const initiatorId = task?.initiator?.id ? String(task.initiator.id) : '';
  const isInitiator = !!(meId && initiatorId && meId === initiatorId);
  // Legacy fallback: no initiator stamped → band founder (artistId === me) may act.
  const canManage = isInitiator || (!initiatorId && !!meId && !!task?.artistId && String(task.artistId) === meId);

  const showApproval = !!task && task.standalone && resolvedStatus === 'needs_review';
  const isTerminal = resolvedStatus === 'done' || resolvedStatus === 'approved';

  const assignedIds = new Set(task ? task.assignees.map((a) => String(a.contactId ?? a.id)) : []);

  return createPortal(
    <div role="presentation">
      <div className="acTd__backdrop" onClick={onClose} />
      <aside className="acTd__drawer" role="dialog" aria-modal="true" aria-label="Task detail">
        <header className="acTd__head">
          <div className="acTd__head-meta">
            <span className="acTd__eyebrow">TASK</span>
            <span className="acTd__release">
              {task && task.releaseTitle
                ? task.releaseTitle + (task.releaseDate ? ' · Drops ' + fmtDate(task.releaseDate) : '')
                : task && !task.standalone ? 'Release task' : '-'}
            </span>
          </div>
          {saving && <span className="acTd__saving">Saving…</span>}
          {canManage && (
            <button type="button" className="acTd__delete" aria-label="Delete task" title="Delete task" onClick={() => setConfirmDelete(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          )}
          <button type="button" className="acTd__close" aria-label="Close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </header>

        {confirmDelete && (
          <div className="acTd__delete-confirm">
            <span>Delete this task? This can&apos;t be undone.</span>
            <button type="button" className="acTd__dc-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button type="button" className="acTd__dc-confirm" onClick={() => void doDelete()}>Yes, delete</button>
          </div>
        )}

        <div className="acTd__body">
          {loading && <div className="acTd__loading">Loading…</div>}
          {!loading && notFound && <div className="acTd__loading">Task not found.</div>}
          {!loading && task && (
            <>
              {isTerminal && (
                <div className="acTd__done-banner">
                  <span className="acTd__done-banner-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </span>
                  <span className="acTd__done-banner-text">
                    <strong>{resolvedStatus === 'approved' ? 'Approved' : 'Completed'}</strong>
                    {task.completed_by && personName(task.completed_by) ? ' by ' + personName(task.completed_by) : ''}
                    {task.completed_at ? ' · ' + fmtDateTime(task.completed_at) : ''}
                    {task.comments.length ? ' · ' + task.comments.length + ' comment' + (task.comments.length === 1 ? '' : 's') + ' on file' : ''}
                  </span>
                  {task.archived_forever ? (
                    <span className="acTd__archive-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Saved
                    </span>
                  ) : (
                    <button type="button" className="acTd__archive-btn" onClick={archiveForever} title="Pin to archive, never auto-delete">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14l-2-5H7z" /><path d="M9 12V5a3 3 0 0 1 6 0v7" /></svg>
                      Save permanently
                    </button>
                  )}
                </div>
              )}

              <textarea
                className="acTd__title"
                defaultValue={task.title}
                placeholder="Untitled task"
                rows={1}
                onBlur={(e) => saveTitle(e.target.value.trim())}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }}
                key={task.id + ':title'}
              />

              {showStatusBadge && (
                <div className="acTd__status-badge" style={{ background: badgeColor + '15', color: badgeColor }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: badgeColor }} />
                  {ALL_STATUS_LABELS[resolvedStatus] || resolvedStatus}
                </div>
              )}

              <div className="acTd__status-row">
                {statusOpts.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={'acTd__status-pill' + (resolvedStatus === opt.key ? ' is-active' : '')}
                    onClick={() => setStatus(opt.key)}
                  >
                    <span className="acTd__dot" style={{ background: opt.dot }} />
                    {opt.label}
                  </button>
                ))}
              </div>

              {showApproval && (
                <div className="acTd__approval">
                  <div className="acTd__approval-head">
                    <span className="acTd__approval-title">⚠ Waiting for sign-off</span>
                  </div>
                  <div className="acTd__approval-body">
                    Assignees marked this ready for review. {personName(task.initiator) || 'The initiator'} needs to approve or request changes.
                  </div>
                  {isInitiator ? (
                    <div className="acTd__approval-actions">
                      <button type="button" className="acTd__btn acTd__btn--approve" onClick={approve}>Approve &amp; close</button>
                      <button type="button" className="acTd__btn acTd__btn--ghost" onClick={requestRevisions}>Request revisions</button>
                    </div>
                  ) : (
                    <div className="acTd__approval-wait">Only {personName(task.initiator) || 'the initiator'} can sign off on this task.</div>
                  )}
                </div>
              )}

              <div className="acTd__meta">
                <div className="acTd__meta-label">Due</div>
                <div className="acTd__meta-value">
                  <input type="date" className="acTd__date-input" defaultValue={task.due} key={task.id + ':due'} onChange={(e) => saveDue(e.target.value)} />
                </div>
                <div className="acTd__meta-label">Assigner</div>
                <div className="acTd__meta-value">
                  {task.initiator && (personName(task.initiator) || task.initiator.id)
                    ? <PersonChip person={task.initiator} />
                    : <span style={{ color: 'var(--ac-color-muted, #6b7280)', fontSize: 12 }}>-</span>}
                </div>
                <div className="acTd__meta-label">Assignees</div>
                <div className="acTd__meta-value" style={{ position: 'relative' }}>
                  {/* Multi-assignee for every task type, click a chip to remove,
                      "+ Add" to append another (release + standalone alike). */}
                  <div className="acTd__assignees-row">
                    {task.assignees.map((a, i) => (
                      <span key={String(a.contactId ?? a.id ?? i)} className="acTd__assignee-chip" onClick={() => removeAssignee(i)} title="Remove" style={{ cursor: 'pointer' }}>
                        <span className="acTd__avatar">{personInitials(a)}<ImgFill src={avatarOf(a)} alt="" /></span>
                        <span>{personName(a)}</span>
                      </span>
                    ))}
                    <PersonChip empty={task.assignees.length ? '+ Add' : '+ Assign team'} onClick={() => setAssigneePicker(true)} />
                  </div>
                  {assigneePicker && (
                    <ContactPicker
                      contacts={task.contacts}
                      exclude={assignedIds}
                      onPick={pickAssignee}
                      onClose={() => setAssigneePicker(false)}
                    />
                  )}
                </div>
              </div>

              {/* Visible to, initiator-only editable pills; read-only "hidden from" otherwise. */}
              {canManage ? (
                <div className="acTd__vis-section">
                  <div className="acTd__vis-label">Visible to <span className="acTd__vis-hint">artist + assignees always see this</span></div>
                  <div className="acTd__vis-row">
                    {(['label', 'management', 'booking'] as const).map((k) => (
                      <button key={k} type="button" className={'acTd__vis-pill' + (task.visibility[k] ? ' is-on' : '')} onClick={() => toggleVisibility(k)}>
                        {k === 'label' ? 'Label' : k === 'management' ? 'Management' : 'Booking'}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                (!task.visibility.label || !task.visibility.management || !task.visibility.booking) && (
                  <div className="acTd__vis-section acTd__vis-section--readonly">
                    <div className="acTd__vis-label">Hidden from</div>
                    <div className="acTd__vis-row">
                      {!task.visibility.label && <span className="acTd__vis-pill acTd__vis-pill--readonly">Label</span>}
                      {!task.visibility.management && <span className="acTd__vis-pill acTd__vis-pill--readonly">Management</span>}
                      {!task.visibility.booking && <span className="acTd__vis-pill acTd__vis-pill--readonly">Booking</span>}
                    </div>
                  </div>
                )
              )}

              {/* Notes */}
              <div className="acTd__section">
                <div className="acTd__section-head"><div className="acTd__section-title">Notes</div></div>
                <textarea
                  className="acTd__notes"
                  defaultValue={task.notes}
                  placeholder="Add notes…"
                  key={task.id + ':notes'}
                  onChange={(e) => {
                    window.clearTimeout(notesTimer.current);
                    const val = e.target.value;
                    notesTimer.current = window.setTimeout(() => saveNotes(val), 600);
                  }}
                  onBlur={(e) => { window.clearTimeout(notesTimer.current); saveNotes(e.target.value); }}
                />
              </div>

              {/* Subtasks */}
              <div className="acTd__section">
                <div className="acTd__section-head">
                  <div className="acTd__section-title">
                    Subtasks <span className="acTd__subprog">{task.subtasks.length ? task.subtasks.filter((s) => s.done).length + '/' + task.subtasks.length : ''}</span>
                  </div>
                  <button type="button" className="acTd__section-action" onClick={() => setShowSubtaskForm((v) => !v)}>+ Add subtask</button>
                </div>
                <div className="acTd__subtasks">
                  {task.subtasks.length ? task.subtasks.map((s) => (
                    <div key={s.id} className={'acTd__subtask' + (s.done ? ' is-done' : '')}>
                      <button type="button" className="acTd__subtask-check" aria-label="Toggle subtask" onClick={() => toggleSubtask(s.id)}>
                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </button>
                      <span className="acTd__subtask-title">{s.title}</span>
                      <button type="button" className="acTd__subtask-del" aria-label="Delete subtask" onClick={() => delSubtask(s.id)}>×</button>
                    </div>
                  )) : <div className="acTd__subtask-empty">No subtasks yet.</div>}
                </div>
                {showSubtaskForm && (
                  <form className="acTd__subtask-form" onSubmit={(e) => { e.preventDefault(); addSubtask(); }}>
                    <input type="text" className="acTd__input" placeholder="Add a subtask…" value={subtaskText} autoFocus onChange={(e) => setSubtaskText(e.target.value)} />
                    <button type="submit" className="acTd__btn">Add</button>
                  </form>
                )}
              </div>

              {/* Links */}
              <div className="acTd__section">
                <div className="acTd__section-head">
                  <div className="acTd__section-title">Links</div>
                  <button type="button" className="acTd__section-action" onClick={() => setShowLinkForm((v) => !v)}>+ Add link</button>
                </div>
                <div className="acTd__links">
                  {task.links.map((l) => {
                    const host = hostnameOf(l.url);
                    const fav = host ? 'https://www.google.com/s2/favicons?sz=32&domain=' + encodeURIComponent(host) : '';
                    return (
                      <a className="acTd__link" key={l.id} href={l.url} target="_blank" rel="noopener noreferrer">
                        {fav ? <img className="acTd__link-favicon" src={fav} alt="" /> : <span className="acTd__link-favicon" />}
                        <span className="acTd__link-label">{l.label || host || l.url}</span>
                        <span className="acTd__link-host">{host}</span>
                        <button type="button" className="acTd__link-remove" title="Remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeLink(l.id); }}>×</button>
                      </a>
                    );
                  })}
                </div>
                {showLinkForm && (
                  <form className="acTd__link-form" onSubmit={(e) => { e.preventDefault(); addLink(); }}>
                    <input type="text" className="acTd__input" placeholder="https://…" value={linkUrl} autoFocus onChange={(e) => setLinkUrl(e.target.value)} />
                    <input type="text" className="acTd__input" placeholder="Label (optional)" style={{ maxWidth: 120 }} value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
                    <button type="submit" className="acTd__btn">Add</button>
                  </form>
                )}
              </div>

              {/* Comments */}
              <div className="acTd__section">
                <div className="acTd__section-head"><div className="acTd__section-title">Comments</div></div>
                <div className="acTd__comments">
                  {task.comments.length ? task.comments.map((c) => (
                    <div className="acTd__comment" key={c.id}>
                      <div className="acTd__comment-head">
                        <span className="acTd__comment-author">{c.actor_name || 'someone'}</span>
                        <span className="acTd__comment-time">{fmtRelTime(c.ts)}</span>
                        {c.id && !c._pending && (
                          <button type="button" className="acTd__comment-remove" title="Delete comment" aria-label="Delete comment" onClick={() => removeComment(c.id)}>×</button>
                        )}
                      </div>
                      <div className="acTd__comment-body">{c.body}</div>
                      {c._failed && c._error && (
                        <div className="acTd__comment-err"><strong>Server:</strong> {c._error}</div>
                      )}
                    </div>
                  )) : <div className="acTd__empty">No comments yet.</div>}
                </div>
                <form className="acTd__comment-form" onSubmit={(e) => { e.preventDefault(); void addComment(); }}>
                  <textarea className="acTd__comment-input" rows={1} placeholder="Leave a comment…" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                  <button type="submit" className="acTd__btn acTd__comment-send" aria-label="Send comment">Comment</button>
                </form>
              </div>

              {/* Activity */}
              {task.activity.length > 0 && (
                <div className="acTd__section">
                  <div className="acTd__section-head"><div className="acTd__section-title">Activity</div></div>
                  <div className="acTd__activity-list">
                    {task.activity.slice().reverse().map((a, i) => (
                      <div className="acTd__activity-row" key={i}>
                        {(a.actor_name || 'someone') + ' ' + activityLabel(a)}
                        <span className="acTd__activity-time">· {fmtRelTime(a.ts)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

// Human label for one activity entry (mirrors legacy activity rendering).
function activityLabel(a: TaskActivity): string {
  const to = a.to == null ? '' : String(a.to);
  const from = a.from == null ? '' : String(a.from);
  switch (a.action) {
    case 'status': return 'changed status: ' + (from || '-') + ' → ' + (to || '-');
    case 'due': return 'set due date to ' + (to ? fmtDate(to) : 'none');
    case 'title': return 'edited title';
    case 'notes': return 'edited notes';
    case 'assignee': return 'assigned to ' + (to || '-');
    case 'assignee_add': return 'added assignee ' + (to || '');
    case 'assignee_remove': return 'removed assignee ' + (from || '');
    case 'done': return to ? 'marked done' : 'reopened';
    case 'link_add': return 'added link "' + (to || '') + '"';
    case 'link_remove': return 'removed link "' + (from || '') + '"';
    case 'visibility': return 'changed visibility';
    case 'archived_forever': return 'pinned to archive';
    default: return a.action;
  }
}

// Re-export the status whitelist so callers can validate before mutating.
export { STANDALONE_STATUS_WHITELIST };
